import PropTypes from 'prop-types'
import {useEffect, useRef, useState} from 'react'

import {CloseIcon, PhotoIcon} from './icons'

import './storyPhotoPrints.scss'

const ARROW_KEYS = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
// A press that moves less than this is a click (see it larger), not a drag.
const DRAG_THRESHOLD_PX = 6

export const photoAlt = photo => `Your photo: ${photo.filename}`

// The print's window: the photo once its cover has loaded, a spinner while it
// loads, and a quiet photo icon if it can't be shown.
const PrintImage = ({photo, src}) => {
  const [loadedSrc, setLoadedSrc] = useState(null)
  const [failedSrc, setFailedSrc] = useState(null)

  if (src === null || (src && failedSrc === src)) return <span className='ls-print-unavailable'><PhotoIcon size={28} /><span className='sr-only'>{photoAlt(photo)}</span></span>

  return (
    <>
      {src && (
        <img
          alt={photoAlt(photo)}
          draggable='false'
          src={src}
          style={loadedSrc === src ? null : {display: 'none'}}
          onError={() => setFailedSrc(src)}
          onLoad={() => setLoadedSrc(src)}
        />
      )}
      {(!src || loadedSrc !== src) && <span className='ls-print-spinner' />}
    </>
  )
}

PrintImage.propTypes = {
  photo: PropTypes.shape({filename: PropTypes.string}).isRequired,
  src: PropTypes.string
}

/**
 * A story's photos as Polaroid-style prints between the chapter label and the
 * question (TK-3108, AC 2.1–3.3, 6.2). One print sits slightly tilted; two or
 * three overlap and fan. Photos still uploading show as loading prints, and a
 * photo that lands while the screen is open "develops" in.
 *
 * Each print opens larger (`onView`), has an always-visible remove button
 * (`onRemove`), and with two or more photos can be dragged onto another, or
 * moved with Alt + an arrow key, to swap them (`onSwap`). Indexes are
 * positions in `photos`, the saved order.
 */
const StoryPhotoPrints = ({photos, uploading = [], images, focusWhenEmpty, onRemove, onSwap, onView}) => {
  const [drag, setDrag] = useState(null)
  const [announcement, setAnnouncement] = useState('')
  const dragRef = useRef(null)
  const suppressClickRef = useRef(false)
  const itemRefs = useRef([])
  const shotRefs = useRef([])
  // Photos on the story when the screen opened print without developing.
  const initialIdsRef = useRef(new Set(photos.map(photo => photo['upload-id'])))
  const pendingFocusRef = useRef(null)

  const count = photos.length + uploading.length

  // Focus follows a removal (to the next photo, or wherever `focusWhenEmpty`
  // sends it when none are left) and a keyboard move (the moved photo), once
  // the list has re-rendered.
  useEffect(() => {
    const pending = pendingFocusRef.current

    if (!pending) return

    pendingFocusRef.current = null

    if (pending.uploadId) {
      const index = photos.findIndex(photo => photo['upload-id'] === pending.uploadId)

      if (shotRefs.current[index]) shotRefs.current[index].focus()

      return
    }

    const next = shotRefs.current[Math.min(pending.removedIndex, photos.length - 1)]

    if (next) next.focus()
    else if (focusWhenEmpty) focusWhenEmpty()
  }, [photos])

  if (count === 0) return null

  const swap = (fromIndex, toIndex, {keepFocus = false} = {}) => {
    if (keepFocus) pendingFocusRef.current = {uploadId: photos[fromIndex]['upload-id']}

    setAnnouncement(`Moved. Photo ${toIndex + 1} of ${photos.length}.`)
    onSwap(fromIndex, toIndex)
  }

  const remove = index => {
    pendingFocusRef.current = {removedIndex: index}
    onRemove(index)
  }

  /* ---------- Reorder: drop a print on another and they trade places ---------- */

  const dropTargetFor = (event, fromIndex) => {
    let target = null
    let closest = Infinity

    photos.forEach((_photo, index) => {
      const element = itemRefs.current[index]

      if (index === fromIndex || !element) return

      const rect = element.getBoundingClientRect()
      const distance = Math.hypot(event.clientX - (rect.left + (rect.width / 2)), event.clientY - (rect.top + (rect.height / 2)))

      if (distance < closest && distance < rect.width * 0.75) {
        closest = distance
        target = index
      }
    })

    return target
  }

  const handlePointerDown = (event, index) => {
    if (photos.length < 2 || event.button > 0 || event.target.closest('.ls-print-remove')) return

    dragRef.current = {index, startX: event.clientX, startY: event.clientY, pointerId: event.pointerId, active: false, target: null}
    if (event.target.setPointerCapture) event.target.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = event => {
    const current = dragRef.current

    if (!current || event.pointerId !== current.pointerId) return

    const dx = event.clientX - current.startX
    const dy = event.clientY - current.startY

    if (!current.active) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return

      current.active = true
      if (event.currentTarget.setPointerCapture) event.currentTarget.setPointerCapture(current.pointerId)
    }

    current.target = dropTargetFor(event, current.index)
    setDrag({index: current.index, dx, dy, target: current.target})
  }

  const endDrag = (event, {cancelled = false} = {}) => {
    const current = dragRef.current

    if (!current || event.pointerId !== current.pointerId) return

    dragRef.current = null

    if (!current.active) return

    // The pointerup ends a drag, not a click on the print.
    suppressClickRef.current = true
    setTimeout(() => { suppressClickRef.current = false }, 0)
    setDrag(null)

    if (!cancelled && current.target !== null) swap(current.index, current.target)
  }

  // Alt + an arrow key swaps with the neighbour on screen (with three photos
  // the first print sits in the middle, so screen order isn't saved order).
  const handleKeyDown = (event, index) => {
    if (!event.altKey || !ARROW_KEYS.includes(event.key) || photos.length < 2) return

    const onScreen = photos
      .map((_photo, photoIndex) => photoIndex)
      .sort((left, right) => itemRefs.current[left].getBoundingClientRect().left - itemRefs.current[right].getBoundingClientRect().left)
    const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1
    const neighbour = onScreen[onScreen.indexOf(index) + step]

    event.preventDefault()
    if (typeof neighbour !== 'number') return
    swap(index, neighbour, {keepFocus: true})
  }

  const view = index => {
    if (suppressClickRef.current) return

    onView(index)
  }

  const printClass = (photo, index) => {
    const classes = ['ls-print']
    const uploadId = photo['upload-id']

    if (!initialIdsRef.current.has(uploadId) && images[uploadId]) classes.push('developing')
    if (drag && drag.index === index) classes.push('dragging')
    if (drag && drag.target === index) classes.push('drop-target')

    return classes.join(' ')
  }

  return (
    <div className='story-photo-prints'>
      <ul aria-label='Photos for this story' className={`ls-prints n${Math.min(count, 3)}${drag ? ' is-dragging' : ''}`}>
        {
          photos.map((photo, index) => {
            const uploadId = photo['upload-id']
            const src = images[uploadId]

            return (
              <li
                className={printClass(photo, index)}
                key={uploadId}
                ref={element => { itemRefs.current[index] = element }}
                style={drag && drag.index === index ? {translate: `${drag.dx}px ${drag.dy}px`} : null}
                onKeyDown={event => handleKeyDown(event, index)}
                onPointerCancel={event => endDrag(event, {cancelled: true})}
                onPointerDown={event => handlePointerDown(event, index)}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}>
                <button
                  aria-describedby={photos.length > 1 ? 'ls-prints-reorder-hint' : null}
                  aria-label={`See photo ${index + 1} larger`}
                  className='ls-print-shot'
                  ref={element => { shotRefs.current[index] = element }}
                  type='button'
                  onClick={() => view(index)}>
                  <PrintImage photo={photo} src={src} />
                </button>
                <button aria-label={`Remove photo ${index + 1}`} className='ls-print-remove' type='button' onClick={() => remove(index)}>
                  <CloseIcon size={14} />
                </button>
              </li>
            )
          })
        }
        {
          uploading.map(entry => (
            <li className='ls-print loading' key={entry.key}>
              <span className='ls-print-shot'>
                <span className='ls-print-spinner' />
                <span className='sr-only'>Adding a photo</span>
              </span>
            </li>
          ))
        }
      </ul>
      {photos.length > 1 && <span className='sr-only' id='ls-prints-reorder-hint'>Drag, or press Alt and an arrow key, to reorder.</span>}
      <div aria-live='polite' className='sr-only'>{announcement}</div>
    </div>
  )
}

StoryPhotoPrints.propTypes = {
  focusWhenEmpty: PropTypes.func,
  images: PropTypes.object.isRequired,
  photos: PropTypes.arrayOf(PropTypes.shape({'upload-id': PropTypes.string, filename: PropTypes.string})).isRequired,
  uploading: PropTypes.arrayOf(PropTypes.shape({key: PropTypes.string, filename: PropTypes.string})),
  onRemove: PropTypes.func.isRequired,
  onSwap: PropTypes.func.isRequired,
  onView: PropTypes.func.isRequired
}

export default StoryPhotoPrints
